"use client";

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { handlePromotions } from "../../services/promotion";
import { successToast, errorToast } from "../../utils/toasters";

export default function PromotionCard() {
  const [showContactForm, setShowContactForm] = useState(false);

  const handlePromoteClick = () => {
    setShowContactForm(true);
    // Prevent background scrolling when popup is open
    if (typeof document !== "undefined") {
      document.body.style.overflow = "hidden";
    }
  };

  const handleCloseForm = () => {
    setShowContactForm(false);
    // Restore scrolling
    if (typeof document !== "undefined") {
      document.body.style.overflow = "";
    }
  };

  return (
    <div className="max-w-sm mx-auto mb-4">
      <div className="relative rounded-3xl overflow-hidden shadow-md bg-[#C6EB75] pt-4 px-4 pb-0">
        {/* Logo */}
        <div className="flex items-center mb-6">
          <div className="bg-white rounded-full p-1 shadow-sm">
            <img
              src="/images/ping-desi-logo.png"
              alt="Gallyo logo"
              width={20}
              height={20}
              className="object-contain"
            />
          </div>
          <span className="text-lg font-bold text-[#7B189F] font-afacad">
            Gallyo
          </span>
        </div>

        {/* Vector and icon container - centered */}
        <div className="relative flex justify-center items-center mb-4">
          {/* Curved vector behind icon */}
          <div className="absolute w-full">
            <img
              src="/images/wave-curve.svg"
              alt="Decorative wave"
              width={300}
              height={60}
              className="object-contain w-full"
            />
          </div>

          {/* Complete icon with wiggly circle - centered */}
          <div className="relative z-10 w-16 h-16">
            <img
              src="/images/complete-icon.svg"
              alt="Icon"
              width={64}
              height={64}
              className="object-contain"
            />
          </div>
        </div>

        {/* Text content */}
        <div className="text-center mb-6">
          <h2 className="text-xl text-[#2D0A3C] leading-tight font-fraunces">
            Let the Community In on
            <br />
            Your Business & Events!
          </h2>
        </div>

        {/* Bottom section with button */}
        <div className="relative flex flex-col items-center">
          {/* Button positioned to sit at the top edge of the purple section */}
          <button
            className="bg-[#7B189F] text-white font-medium py-2.5 px-10 rounded-full border-2 border-white shadow-md relative z-10 flex items-center justify-center font-afacad text-base"
            style={{
              width: "160px",
              marginBottom: "-1.25rem", // Adjusted overlap for larger button
            }}
            onClick={handlePromoteClick}
          >
            Promote
          </button>

          {/* Bottom purple section using SVG - 90% width and centered, extending to bottom */}
          <div className="w-[90%]">
            <div className="relative w-full" style={{ height: "60px" }}>
              <img
                src="/images/bottom-curve.svg"
                alt="Purple background"
                className="object-cover object-top w-full h-full absolute"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Contact Form Popup - Using Portal */}
      {showContactForm && <PopupPortal onClose={handleCloseForm} />}
    </div>
  );
}

// Portal component to render popup at document root
function PopupPortal({ onClose }) {
  // Only render the portal on the client side
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);

    // Handle ESC key press to close popup
    const handleEscKey = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscKey);

    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [onClose]);

  // Create a portal container if it doesn't exist
  useEffect(() => {
    if (typeof document !== "undefined") {
      // Check if portal container already exists
      let portalContainer = document.getElementById("popup-portal-container");

      if (!portalContainer) {
        // Create portal container if it doesn't exist
        portalContainer = document.createElement("div");
        portalContainer.id = "popup-portal-container";

        // Apply styles to ensure it's above everything
        portalContainer.style.position = "fixed";
        portalContainer.style.top = "0";
        portalContainer.style.left = "0";
        portalContainer.style.width = "100%";
        portalContainer.style.height = "100%";
        portalContainer.style.zIndex = "2147483647"; // Maximum possible z-index
        portalContainer.style.pointerEvents = "none"; // Allow clicks to pass through by default

        document.body.appendChild(portalContainer);
      }
    }
  }, []);

  if (!mounted) return null;

  // Render the popup in the portal container
  return createPortal(
    <ContactFormPopup onClose={onClose} />,
    document.getElementById("popup-portal-container") || document.body
  );
}

function ContactFormPopup({ onClose }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    description: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await handlePromotions(formData);

      // Show success message
      successToast("Promotion request submitted successfully!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      // Reset form data
      setFormData({
        name: "",
        email: "",
        phone: "",
        description: "",
      });

      // Close the form only after successful submission
      // Add a small delay to let user see the success message
      setTimeout(() => {
        onClose();
      }, 2000); // Close after 2 seconds
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error ||
        error.message ||
        "Something went wrong. Please try again.";

      errorToast(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      console.error("Error submitting form:", error);
      // Don't close the form on error - let user try again
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle click outside to close
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm font-afacad flex items-center justify-center p-4 animate-in fade-in duration-300 overflow-scroll"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2147483647, // Maximum possible z-index
        pointerEvents: "auto", // Enable pointer events for the popup
      }}
      onClick={handleBackdropClick}
    >
      <div
        className="relative w-full max-w-md bg-white rounded-2xl shadow-xl transform transition-all overflow-scroll"
        style={{
          position: "relative",
          zIndex: 2147483647, // Maximum possible z-index
          margin: "auto", // Center the popup
        }}
        onClick={(e) => e.stopPropagation()} // Prevent clicks from reaching backdrop
      >
        {/* Close Button */}
        <button
          className="absolute top-4 right-4 z-20 bg-white rounded-full p-1 hover:bg-gray-100 transition-colors"
          onClick={onClose}
          aria-label="Close"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="#666666"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Form Content */}
        <div className="p-6 pb-0">
          {/* Logo and Title */}
          <div className="flex items-center gap-2 mb-3">
            <img
              src="/images/desi360-logo.png"
              alt="Gallyo logo"
              width={20}
              height={20}
              className="object-contain"
            />
            <span className="text-[#9C27B0] font-medium">Gallyo</span>
          </div>

          <h2 className="text-xl text-[#2D0A3C] font- font-fraunces mb-1">
            Take the spotlight — promote your event or biz
          </h2>
          <p className="text-gray-600 text-sm mb-5">
            Fill out the form below and we'll help you reach the community
          </p>
        </div>

        {/* Contact Form */}
        <form onSubmit={handleSubmit} className="px-6 pt-0">
          <div className="space-y-4">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#9C27B0] focus:border-[#9C27B0] disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Your name"
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#9C27B0] focus:border-[#9C27B0] disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="your.email@example.com"
              />
            </div>

            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Phone
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                disabled={isSubmitting}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#9C27B0] focus:border-[#9C27B0] disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="(123) 456-7890"
              />
            </div>

            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Tell us about your business/event
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                required
                disabled={isSubmitting}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#9C27B0] focus:border-[#9C27B0] resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Describe your business or event..."
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#9C27B0] text-white font-medium py-3 rounded-lg mb-6 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Submitting...
                </>
              ) : (
                "Submit Promotion Request"
              )}
            </button>
          </div>
        </form>

        {/* App Download Section */}
        <div className="bg-[#F8F7F2] p-5">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0">
              <div className="relative">
                <img
                  src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Frame%20%282%29-XR9vuZgw0jiH5tHGbdz8su2qwZPmSr.svg"
                  alt="Gallyo App"
                  width={50}
                  height={60}
                  className="object-contain"
                />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-sm text-[#2B3F01] mb-1">
                Discover all things Desi
              </h3>
              <p className="text-gray-700 text-xs mb-2">
                Download our app for even more promotion features and community
                engagement!
              </p>
              <div className="flex flex-wrap gap-2">
                <a
                  href="https://apps.apple.com/us/app/gallyo/id6745209299"
                  className="h-8 transform hover:scale-105 transition-transform"
                >
                  <img
                    src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/download-on-the-app-store-apple-logo-svgrepo-com%201-IIS3UHJPhxe7pkO2z90baSIKjxO9w7.svg"
                    alt="Download on App Store"
                    width={110}
                    height={32}
                    className="h-full w-auto"
                  />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.leantechop.pingdesi&pcampaignid=web_share"
                  className="h-8 transform hover:scale-105 transition-transform"
                >
                  <img
                    src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Google_Play_Store_badge_EN%201-pC0zVKtBIj1Q7E7WGp1juUFzixpqP4.svg"
                    alt="Get it on Google Play"
                    width={110}
                    height={32}
                    className="h-full w-auto"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
